import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  AlertTitle,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

// Future component imports
// import ParetoChart from '../../components/dashboard/ParetoChart';
// import SupplierCountDistributionChart from '../../components/dashboard/SupplierCountDistributionChart';
// import AdminOverheadChart from '../../components/dashboard/AdminOverheadChart';
// import TailSpendSavingsCalculator from '../../components/dashboard/TailSpendSavingsCalculator';

interface SupplierSegment {
  segment: string;
  supplierCount: number;
  percentOfSuppliers: number;
  spend: number;
  percentOfSpend: number;
  averageTransactionsPerSupplier: number;
  transactions: number;
  percentOfTransactions: number;
  adminCostPerTransaction: number;
  totalAdminCost: number;
  percentOfAdminCost: number;
}

const TailSpendPage: React.FC = () => {
  const theme = useTheme();

  // Mock data for demonstration
  const [supplierSegments, setSupplierSegments] = useState<SupplierSegment[]>([
    {
      segment: 'Top 20% (Strategic)',
      supplierCount: 38,
      percentOfSuppliers: 20,
      spend: 34500000,
      percentOfSpend: 69,
      averageTransactionsPerSupplier: 152,
      transactions: 5776,
      percentOfTransactions: 42,
      adminCostPerTransaction: 12,
      totalAdminCost: 69312,
      percentOfAdminCost: 25
    },
    {
      segment: 'Middle 30% (Tactical)',
      supplierCount: 57,
      percentOfSuppliers: 30,
      spend: 10500000,
      percentOfSpend: 21,
      averageTransactionsPerSupplier: 82,
      transactions: 4674,
      percentOfTransactions: 34,
      adminCostPerTransaction: 18,
      totalAdminCost: 84132,
      percentOfAdminCost: 30
    },
    {
      segment: 'Bottom 50% (Tail)',
      supplierCount: 95,
      percentOfSuppliers: 50,
      spend: 5000000,
      percentOfSpend: 10,
      averageTransactionsPerSupplier: 35,
      transactions: 3325,
      percentOfTransactions: 24,
      adminCostPerTransaction: 38,
      totalAdminCost: 126350,
      percentOfAdminCost: 45
    }
  ]);

  // Business Rules Implementation
  const paretoThreshold = 20; // Expected % of suppliers for 80% of spend
  const tailSpendSupplierCountThreshold = 50; // Flag if tail spend has more than 50% of suppliers
  const adminOverheadThreshold = 30; // Flag if admin cost > 30% of spend for tail

  // Calculate actual pareto ratio
  const top20PercentSpend = supplierSegments[0].percentOfSpend;
  const paretoRatio = top20PercentSpend / 80; // Actual vs ideal 80/20 rule
  
  // Check if tail spend has too many suppliers
  const tailSupplierSegment = supplierSegments[2]; // Bottom 50%
  const tailSupplierCount = tailSupplierSegment.supplierCount;
  const tailSupplierCountTooHigh = tailSupplierSegment.percentOfSuppliers > tailSpendSupplierCountThreshold;
  
  // Check if admin overhead exceeds savings
  const tailAdminCost = tailSupplierSegment.totalAdminCost;
  const tailSpend = tailSupplierSegment.spend;
  const adminOverheadPercentage = (tailAdminCost / tailSpend) * 100;
  const adminOverheadTooHigh = adminOverheadPercentage > adminOverheadThreshold;

  // Alerts based on business rules
  const paretoAlert = paretoRatio < 0.9 && ( // If less than 90% of the ideal 80/20 rule
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>Pareto Distribution Analysis</AlertTitle>
      Top 20% of suppliers account for {top20PercentSpend}% of total spend, 
      which is {(paretoRatio * 100).toFixed(1)}% of the ideal 80/20 rule. 
      {paretoRatio < 0.75 && " This indicates significant spend fragmentation."}
    </Alert>
  );

  const tailSupplierAlert = tailSupplierCountTooHigh && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Tail Spend Supplier Count</AlertTitle>
      The tail spend segment has {tailSupplierCount} suppliers ({tailSupplierSegment.percentOfSuppliers}% of total)
      but only accounts for {tailSupplierSegment.percentOfSpend}% of total spend.
      Potential opportunity for supplier consolidation.
    </Alert>
  );

  const adminOverheadAlert = adminOverheadTooHigh && (
    <Alert severity="error" sx={{ mt: 2 }}>
      <AlertTitle>Administrative Overhead</AlertTitle>
      Administrative costs for tail spend suppliers are approximately ${tailAdminCost.toLocaleString()},
      which is {adminOverheadPercentage.toFixed(1)}% of the actual tail spend (${tailSpend.toLocaleString()}).
      Immediate action recommended as overhead exceeds potential cost savings.
    </Alert>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Tail Spend Analysis
      </Typography>

      <Grid container spacing={3}>
        {/* Pareto Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Pareto Distribution of Spend Across Suppliers
              {/* <ParetoChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Supplier Count Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Supplier Count Distribution
              {/* <SupplierCountDistributionChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Administrative Overhead Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Administrative Overhead by Supplier Segment
              {/* <AdminOverheadChart /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Tail Spend Insights */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tail Spend Insights
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {paretoAlert}
              {tailSupplierAlert}
              {adminOverheadAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* Supplier Segments Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Supplier Segment Analysis
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Segment</TableCell>
                      <TableCell align="right">Suppliers</TableCell>
                      <TableCell align="right">% of Suppliers</TableCell>
                      <TableCell align="right">Spend</TableCell>
                      <TableCell align="right">% of Spend</TableCell>
                      <TableCell align="right">Admin Cost</TableCell>
                      <TableCell align="right">Admin as % of Spend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplierSegments.map((segment, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          backgroundColor: index === 2 
                            ? 'rgba(255, 152, 0, 0.1)' // Highlight tail spend row
                            : 'inherit'
                        }}
                      >
                        <TableCell>{segment.segment}</TableCell>
                        <TableCell align="right">{segment.supplierCount}</TableCell>
                        <TableCell align="right">{segment.percentOfSuppliers}%</TableCell>
                        <TableCell align="right">${(segment.spend / 1000000).toFixed(1)}M</TableCell>
                        <TableCell align="right">{segment.percentOfSpend}%</TableCell>
                        <TableCell align="right">${segment.totalAdminCost.toLocaleString()}</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            color: (segment.totalAdminCost / segment.spend) * 100 > adminOverheadThreshold
                              ? theme.palette.error.main 
                              : 'inherit'
                          }}
                        >
                          {((segment.totalAdminCost / segment.spend) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TailSpendPage; 